import {
  Home,
  FileStack,
  Calendar,
  Users,
  Container,
  Settings,
  UsersRound,
  HelpCircle,
  BookOpen,
  Clock,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

export interface KBSection {
  id: string;
  title: string;
  category: "getting-started" | "core-data" | "operations" | "quality" | "admin" | "help";
  icon: LucideIcon;
  keywords: string[];
  content: React.ReactNode;
}

export interface KBCategory {
  id: string;
  title: string;
  sections: string[]; // section IDs
}

export const categories: KBCategory[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    sections: ["dashboard", "navigation"],
  },
  {
    id: "core-data",
    title: "Core Data",
    sections: ["clients", "wine-lots", "vessels"],
  },
  {
    id: "operations",
    title: "Operations",
    sections: ["operations-planner", "documents", "tank-planning"],
  },
  {
    id: "admin",
    title: "Administration",
    sections: ["user-management", "settings"],
  },
  {
    id: "help",
    title: "Help & Support",
    sections: ["getting-help", "faq", "tips-tricks", "changelog"],
  },
];

export const knowledgeBaseContent: KBSection[] = [
  // Getting Started
  {
    id: "dashboard",
    title: "Dashboard",
    category: "getting-started",
    icon: Home,
    keywords: ["dashboard", "home", "overview", "statistics", "stats", "metrics", "kpi", "real-time"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Your command center providing at-a-glance visibility into all critical metrics and activities.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Features:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Active Operations:</strong> View ongoing cellar operations</li>
            <li><strong>Intake Requests:</strong> New customer requests awaiting review</li>
            <li><strong>Client Summary:</strong> Quick stats on your client base</li>
            <li><strong>Vessel Capacity:</strong> Monitor available tank/barrel space</li>
            <li><strong>Real-time Updates:</strong> Live data refreshes automatically</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "navigation",
    title: "Navigation & Layout",
    category: "getting-started",
    icon: BookOpen,
    keywords: ["navigation", "sidebar", "menu", "layout", "quick start", "getting started"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Learn how to navigate the application and find what you need quickly.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Quick Start:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Navigate using the sidebar menu</li>
            <li>Start with Dashboard for an overview</li>
            <li>Check Documents for scanned files needing review</li>
            <li>Track operations in real-time</li>
          </ul>
        </div>
        <div className="space-y-2">
          <h4 className="font-medium">Key Features:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Automated email ingestion</li>
            <li>Real-time collaboration</li>
            <li>Comprehensive audit trails</li>
            <li>QC management</li>
            <li>Client portal access</li>
          </ul>
        </div>
      </div>
    ),
  },

  // Core Data
  {
    id: "clients",
    title: "Client Management",
    category: "core-data",
    icon: Users,
    keywords: ["clients", "customers", "contacts", "profiles", "portal", "statistics"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Comprehensive client relationship and contact management.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Features:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Complete contact information and profiles</li>
            <li>Real-time statistics (active lots, total volume)</li>
            <li>Client-specific wine lot views</li>
            <li>Portal access for customer self-service</li>
            <li>Search and filtering capabilities</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "wine-lots",
    title: "Wine Lot Management",
    category: "core-data",
    icon: Container,
    keywords: ["wine lots", "lots", "inventory", "varietals", "vintage", "tracking", "volume"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Track wine lots from intake through bottling with complete traceability.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Tracking Capabilities:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Varietal and vintage information</li>
            <li>Current volume and capacity monitoring</li>
            <li>Client associations</li>
            <li>Operation history and lineage</li>
            <li>Lot detail views with complete audit trail</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "vessels",
    title: "Vessel Management",
    category: "core-data",
    icon: Container,
    keywords: ["vessels", "tanks", "barrels", "capacity", "inventory", "stainless", "oak", "puncheons"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Complete vessel inventory with capacity tracking and utilization monitoring.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Vessel Types:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Tanks (Stainless Steel, Concrete)</li>
            <li>Barrels (Oak - French, American, Hungarian)</li>
            <li>Puncheons</li>
            <li>Totes and IBCs</li>
            <li>Custom vessel types</li>
          </ul>
        </div>
        <div className="space-y-2">
          <h4 className="font-medium">Tracking:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Capacity and current volume</li>
            <li>Material type and age</li>
            <li>Location in cellar</li>
            <li>Operation history per vessel</li>
            <li>Utilization analytics</li>
          </ul>
        </div>
      </div>
    ),
  },

  // Operations
  {
    id: "operations-planner",
    title: "Operations Planner",
    category: "operations",
    icon: Calendar,
    keywords: ["operations", "scheduling", "calendar", "planner", "racking", "bottling", "blending", "filtering"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Intelligent scheduling system for all winery operations with calendar and list views.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Operation Types:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Racking:</strong> Move wine between vessels</li>
            <li><strong>Bottling:</strong> Final packaging operations</li>
            <li><strong>Blending:</strong> Combine multiple lots</li>
            <li><strong>Filtering:</strong> Clarification processes</li>
            <li><strong>Addition:</strong> Sulfite, enzymes, nutrients, etc.</li>
            <li><strong>Sampling:</strong> Pull samples from a vessel</li>
          </ul>
        </div>
        <div className="space-y-2">
          <h4 className="font-medium">Features:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Source and destination vessel tracking</li>
            <li>Volume and unit management (gallons, liters, barrels)</li>
            <li>Automated volume updates on completion</li>
            <li>Operation dependencies and sequencing</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "documents",
    title: "Documents",
    category: "operations",
    icon: FileStack,
    keywords: ["documents", "scanned", "pdf", "routing", "dropbox", "inbox", "review", "parsed", "routed", "file management"],
    content: (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">Dropbox Integration</Badge>
          <Badge variant="secondary">AI Parsing</Badge>
        </div>
        <p className="text-muted-foreground">
          Automated document processing pipeline that scans your Dropbox inbox, parses PDFs with AI, and routes them to the correct client folders.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Dashboard Metrics:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Routed Today:</strong> Click to view all successfully routed documents with PDF preview</li>
            <li><strong>Needs Review:</strong> Documents requiring manual review or approval</li>
            <li><strong>In Progress:</strong> Documents currently being detected, parsed, or routed</li>
            <li><strong>Failed:</strong> Documents that encountered errors during processing</li>
          </ul>
        </div>
        <div className="space-y-2">
          <h4 className="font-medium">Workflow:</h4>
          <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
            <li>Click "Scan Inbox" to detect new files in Dropbox inbox</li>
            <li>AI automatically parses document type, date, and client</li>
            <li>Review documents in the Needs Review queue</li>
            <li>Approve the suggested route or edit the destination path</li>
            <li>Document is moved to the correct Dropbox folder automatically</li>
          </ol>
        </div>
        <div className="space-y-2">
          <h4 className="font-medium">Review Panel:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>PDF Preview:</strong> View the document inline before routing</li>
            <li><strong>Parsed Details:</strong> See AI-extracted document type, date, and action</li>
            <li><strong>Client Match:</strong> Verify or change the matched client</li>
            <li><strong>Destination Path:</strong> Edit the target Dropbox folder path</li>
            <li><strong>Confidence Score:</strong> See how confident the AI is in its parsing</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "tank-planning",
    title: "Tank Planning",
    category: "operations",
    icon: Clock,
    keywords: ["tank planning", "timeline", "assignments", "transfers", "vessel tracking", "out reason", "completion"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Visual timeline view for planning and tracking wine lot assignments to vessels over time.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Features:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Timeline View:</strong> Visual representation of vessel assignments over time</li>
            <li><strong>Assignment Management:</strong> Assign wine lots to vessels with start/end dates</li>
            <li><strong>Transfer Planning:</strong> Plan movements between vessels</li>
            <li><strong>Vessel Utilization:</strong> See availability and capacity at a glance</li>
          </ul>
        </div>
        <div className="space-y-2">
          <h4 className="font-medium">Assignment Completion:</h4>
          <p className="text-muted-foreground mb-2">
            When completing an assignment, you can specify the reason:
          </p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Transferred:</strong> Wine was moved to another vessel</li>
            <li><strong>Bottled:</strong> Wine was bottled from this vessel</li>
            <li><strong>Blended:</strong> Wine was blended into another lot</li>
            <li><strong>Sold:</strong> Wine was sold in bulk</li>
            <li><strong>Other:</strong> Custom reason with notes</li>
          </ul>
        </div>
      </div>
    ),
  },

  // Administration
  {
    id: "user-management",
    title: "User Management",
    category: "admin",
    icon: UsersRound,
    keywords: ["users", "team", "roles", "permissions", "invitations", "admin", "manager", "operator"],
    content: (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge>Admin Only</Badge>
        </div>
        <p className="text-muted-foreground">
          Complete user lifecycle management with role-based access control.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">User Roles:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Admin:</strong> Full system access and configuration</li>
            <li><strong>Manager:</strong> Operations, client management</li>
            <li><strong>Operator:</strong> Execute operations, update status</li>
            <li><strong>Client:</strong> Portal access to own data only</li>
          </ul>
        </div>
        <div className="space-y-2">
          <h4 className="font-medium">Features:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Email invitation system</li>
            <li>Role assignment and management</li>
            <li>User activation/deactivation (soft delete)</li>
            <li>Beta access code management</li>
            <li>Pending invitations tracking</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "settings",
    title: "Settings & Configuration",
    category: "admin",
    icon: Settings,
    keywords: ["settings", "configuration", "profile", "organization", "integrations", "preferences", "notifications", "audit"],
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Multi-tab settings interface for personal and organization-wide configuration.
        </p>
        <div className="space-y-2">
          <h4 className="font-medium">Settings Tabs:</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Profile:</strong> User profile editing, avatar upload</li>
            <li><strong>Organization:</strong> Company settings, logo, timezone, units</li>
            <li><strong>Integrations:</strong> Third-party connections (Vintrace, etc.)</li>
            <li><strong>Preferences:</strong> User preferences and defaults</li>
            <li><strong>Notifications:</strong> Email and in-app notification settings</li>
            <li><strong>Audit Logs:</strong> Complete system audit trail (admin only)</li>
          </ul>
        </div>
      </div>
    ),
  },

  // Help & Support
  {
    id: "getting-help",
    title: "Getting Help",
    category: "help",
    icon: HelpCircle,
    keywords: ["help", "support", "contact", "administrator", "email"],
    content: (
      <div className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-medium">Contact Support</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Administrator:</strong> Contact your Gravity Assist admin</li>
            <li><strong>Email:</strong> brandon@hcreativemedia.com</li>
            <li><strong>Documentation:</strong> This page (Settings → Documentation)</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "faq",
    title: "Frequently Asked Questions",
    category: "help",
    icon: HelpCircle,
    keywords: ["faq", "questions", "common", "password", "access", "email", "missing"],
    content: (
      <div className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-medium">Common Questions</h4>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li><strong>Forgot Password?</strong> Use "Forgot Password" link on login page</li>
            <li><strong>Can't Access Feature?</strong> Check with admin about your role permissions</li>
            <li><strong>Data Missing?</strong> Check filters and search - data may be archived</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "tips-tricks",
    title: "Tips & Tricks",
    category: "help",
    icon: BookOpen,
    keywords: ["tips", "tricks", "shortcuts", "keyboard", "navigation", "search", "filters"],
    content: (
      <div className="space-y-4">
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li><strong>Keyboard Navigation:</strong> Use Tab key to move between fields quickly</li>
          <li><strong>Search:</strong> Most pages have search - use it to find records fast</li>
          <li><strong>Filters:</strong> Combine filters for precise results</li>
          <li><strong>Inline Editing:</strong> Many fields can be edited directly in detail sheets</li>
          <li><strong>Real-time Updates:</strong> No need to refresh - changes appear automatically</li>
          <li><strong>Sidebar:</strong> Click logo to collapse/expand sidebar for more space</li>
        </ul>
      </div>
    ),
  },
  {
    id: "changelog",
    title: "Changelog",
    category: "help",
    icon: Clock,
    keywords: ["changelog", "updates", "version", "release", "new features", "history"],
    content: (
      <div className="space-y-6">
        {/* Version 1.4.0 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">Version 1.4.0</h4>
            <Badge variant="secondary">February 5, 2026</Badge>
            <Badge>Latest</Badge>
          </div>
          <div className="space-y-2 text-muted-foreground">
            <p className="font-medium text-sm">Document Processing Workflow</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Documents Page:</strong> New streamlined single-view layout replacing tabbed interface</li>
              <li><strong>Dropbox Integration:</strong> Scan inbox, parse, and route scanned documents automatically</li>
              <li><strong>AI Parsing:</strong> Automatic extraction of document type, date, client, and routing path</li>
              <li><strong>Needs Review Queue:</strong> Dedicated queue for documents requiring manual approval</li>
              <li><strong>Routed Today Dialog:</strong> Split-pane view with PDF preview and metadata for routed files</li>
              <li><strong>Auto Folder Creation:</strong> Missing Dropbox destination folders are created automatically</li>
              <li><strong>PDF Viewer:</strong> Inline document preview with zoom and page navigation</li>
            </ul>
          </div>
        </div>

        {/* Version 1.3.0 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">Version 1.3.0</h4>
            <Badge variant="secondary">December 16, 2025</Badge>
          </div>
          <div className="space-y-2 text-muted-foreground">
            <p className="font-medium text-sm">Work Order Chat Threads</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Real-time Messaging:</strong> Built-in chat for each work order</li>
              <li><strong>File Attachments:</strong> Share documents directly in conversations</li>
              <li><strong>Staff & Customer Views:</strong> Distinct messaging experience for team and clients</li>
              <li><strong>Customer Portal:</strong> Secure magic link access for customer messaging</li>
              <li><strong>Message History:</strong> Complete conversation preservation</li>
            </ul>
            <p className="font-medium text-sm mt-3">Email Infrastructure Update</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>New Email Address:</strong> workorders@grvtylabs.com</li>
              <li><strong>Cloudflare Email Worker:</strong> Edge-based email routing and processing</li>
              <li><strong>Netlify Functions:</strong> Serverless email-to-work-order pipeline</li>
              <li>Improved reliability and faster email processing</li>
            </ul>
          </div>
        </div>

        {/* Version 1.2.0 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">Version 1.2.0</h4>
            <Badge variant="secondary">October 12, 2025</Badge>
          </div>
          <div className="space-y-2 text-muted-foreground">
            <p className="font-medium text-sm">Analytics & Insights</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>PostHog Integration:</strong> Comprehensive product analytics with 40+ event types</li>
              <li><strong>User Tracking:</strong> Automatic user identification and session tracking</li>
              <li><strong>Event Tracking:</strong> Work orders, operations, intake funnel, lab tests</li>
              <li><strong>Navigation Analytics:</strong> Automatic page view and feature access tracking</li>
              <li><strong>Error Tracking:</strong> Global error boundary with detailed error capture</li>
              <li><strong>Performance Metrics:</strong> Duration tracking for operations and workflows</li>
            </ul>
          </div>
        </div>

        {/* Version 1.1.0 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">Version 1.1.0</h4>
            <Badge variant="secondary">October 10, 2025</Badge>
          </div>
          <div className="space-y-2 text-muted-foreground">
            <p className="font-medium text-sm">Major Features</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>User Management System:</strong> Complete user lifecycle with invitations</li>
              <li><strong>Beta Access:</strong> Code management and signup tracking</li>
              <li><strong>Settings Hub:</strong> 6-tab interface for configuration</li>
              <li><strong>Vintrace Integration:</strong> Sync dashboard and conflict resolution</li>
              <li><strong>Password Reset:</strong> Secure reset flow with email verification</li>
              <li><strong>Avatar & Logo Upload:</strong> User and organization branding</li>
            </ul>
            <p className="font-medium text-sm mt-3">Security</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Enhanced RLS policies for sensitive data</li>
              <li>Database function security hardening</li>
              <li>Storage bucket policies for avatars and logos</li>
              <li>Soft delete implementation for users</li>
            </ul>
          </div>
        </div>

        {/* Version 1.0.0 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">Version 1.0.0</h4>
            <Badge variant="secondary">October 1, 2025</Badge>
          </div>
          <div className="space-y-2 text-muted-foreground">
            <p className="font-medium text-sm">Initial Release</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Core Features:</strong> Work orders, operations, clients, vessels</li>
              <li><strong>Document Processing:</strong> Scanned document intake and routing</li>
              <li><strong>Lab Testing:</strong> QC management and sample tracking</li>
              <li><strong>Real-time Updates:</strong> Live collaboration support</li>
              <li><strong>Audit Logging:</strong> Complete change tracking</li>
              <li><strong>42+ Database Tables:</strong> Complete data model</li>
              <li><strong>Theme Support:</strong> Light/Dark/System modes</li>
            </ul>
          </div>
        </div>
      </div>
    ),
  },
];

// Helper to get section by ID
export function getSectionById(id: string): KBSection | undefined {
  return knowledgeBaseContent.find((section) => section.id === id);
}

// Helper to get sections by category
export function getSectionsByCategory(categoryId: string): KBSection[] {
  return knowledgeBaseContent.filter((section) => section.category === categoryId);
}
